import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { List, Avatar, Card, Typography, Row, Col, Tag, Space, Badge, Tabs, Button } from 'antd';
import { BellOutlined, RiseOutlined, WarningOutlined, ClockCircleOutlined, DashboardOutlined, LeftOutlined, DollarOutlined, ShopOutlined, AppstoreOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

// 通知数据接口
interface NotificationItem {
  id: string;
  type: 'inventory' | 'orderSpeed' | 'priceChange';
  subType?: 'timePoint' | 'competitor' | 'roomStatus'; // 调价提醒的子类型
  title: string;
  content: string;
  time: string;
  store: string;
  status: 'warning' | 'alert' | 'info';
  isRead: boolean;
  additionalInfo?: {
    warningDay?: string;
    inventoryStatus?: string;
    orderIncrease?: number;
    priceChange?: number; // 价格变化百分比或具体金额
    targetDate?: string; // 目标日期
    competitorName?: string; // 竞品名称
  };
}

const InventoryAlertNotifications: React.FC = () => {
  const navigate = useNavigate();
  
  // 模拟通知数据，实际项目中应从API获取
  const [notifications, setNotifications] = useState<NotificationItem[]>([
    {
      id: '1',
      type: 'inventory',
      title: '库存预警通知',
      content: '7月1日的库存即将售罄，建议关注并及时调整售价策略',
      time: '2023-06-29 14:30',
      store: '秋果花园酒店（西湖店）',
      status: 'warning',
      isRead: false,
      additionalInfo: {
        warningDay: '2023-07-01',
        inventoryStatus: '即将售罄'
      }
    },
    {
      id: '2',
      type: 'inventory',
      title: '库存预警通知',
      content: '7月15日的库存低于30%，可能存在涨价空间',
      time: '2023-06-28 09:15',
      store: '秋果花园酒店（西湖店）',
      status: 'info',
      isRead: true,
      additionalInfo: {
        warningDay: '2023-07-15',
        inventoryStatus: '库存偏低'
      }
    },
    {
      id: '3',
      type: 'orderSpeed',
      title: '订单进单速度异常提醒',
      content: '您的酒店7月15-21日期间订单进单速度明显加快，请及时关注',
      time: '2023-07-10 09:30',
      store: '秋果花园酒店（西湖店）',
      status: 'alert',
      isRead: false,
      additionalInfo: {
        orderIncrease: 35
      }
    },
    {
      id: '4',
      type: 'orderSpeed',
      title: '周末订单进单速度提升',
      content: '下周末订单进单速度较平日提升40%，建议考虑周末差异化定价',
      time: '2023-07-05 16:45',
      store: '秋果花园酒店（西湖店）',
      status: 'info',
      isRead: true,
      additionalInfo: {
        orderIncrease: 40
      }
    },
    {
      id: '5',
      type: 'orderSpeed',
      title: '假日订单速度预测提醒',
      content: '国庆节假期订单速度预计提升50%，建议提前调整价格策略以优化收益',
      time: '2023-09-15 08:30',
      store: '秋果花园酒店（西湖店）',
      status: 'info',
      isRead: false,
      additionalInfo: {
        orderIncrease: 50
      }
    },
    // 新增调价提醒示例
    {
      id: '6',
      type: 'priceChange',
      subType: 'timePoint',
      title: '时间点调价提醒',
      content: '系统检测到7月27-28日为周末高峰，建议提前调高房价以提升收益',
      time: '2023-07-20 10:15',
      store: '秋果花园酒店（西湖店）',
      status: 'info',
      isRead: false,
      additionalInfo: {
        priceChange: 15, // 建议上调15%
        targetDate: '2023-07-27'
      }
    },
    {
      id: '7',
      type: 'priceChange',
      subType: 'competitor',
      title: '竞品联动调价提醒',
      content: '竞品"西湖雅园酒店"已上调7月30日房价至¥888，建议适当跟进调价',
      time: '2023-07-21 16:30',
      store: '秋果花园酒店（西湖店）',
      status: 'alert',
      isRead: false,
      additionalInfo: {
        priceChange: 80, // 建议上调80元
        targetDate: '2023-07-30',
        competitorName: '西湖雅园酒店'
      }
    },
    {
      id: '8',
      type: 'priceChange',
      subType: 'roomStatus',
      title: '房态变化调价提醒',
      content: '8月2日豪华大床房库存剩余不足20%，建议上调价格优化收益',
      time: '2023-07-22 09:45',
      store: '秋果花园酒店（西湖店）',
      status: 'warning',
      isRead: false,
      additionalInfo: {
        priceChange: 20, // 建议上调20%
        targetDate: '2023-08-02'
      }
    }
  ]);

  // 点击通知项处理
  const handleNotificationClick = (notification: NotificationItem) => {
    // 标记为已读
    setNotifications(prevNotifications => 
      prevNotifications.map(item => 
        item.id === notification.id ? { ...item, isRead: true } : item
      )
    );
    
    // 根据通知类型导航到不同的详情页
    if (notification.type === 'inventory') {
      navigate(`/mobile-preview/inventory-alert/${notification.id}`);
    } else if (notification.type === 'orderSpeed') {
      navigate(`/mobile-preview/order-speed-alert/${notification.id}`);
    } else if (notification.type === 'priceChange') {
      // 根据子类型可以导航到不同的调价详情页
      navigate(`/mobile-preview/price-change-alert/${notification.id}`);
    }
  };

  // 获取通知图标
  const getNotificationIcon = (notification: NotificationItem) => {
    if (notification.type === 'inventory') {
      return (
        <Avatar size="large" style={{ backgroundColor: notification.status === 'warning' ? '#ff4d4f' : '#52c41a' }}>
          <WarningOutlined style={{ fontSize: '18px' }} />
        </Avatar>
      );
    } else if (notification.type === 'orderSpeed') {
      return (
        <Avatar size="large" style={{ backgroundColor: notification.status === 'alert' ? '#faad14' : '#1890ff' }}>
          <RiseOutlined style={{ fontSize: '18px' }} />
        </Avatar>
      );
    } else if (notification.type === 'priceChange') {
      // 根据调价提醒的子类型展示不同图标
      let icon;
      let color;
      
      switch (notification.subType) {
        case 'timePoint':
          icon = <ClockCircleOutlined style={{ fontSize: '18px' }} />;
          color = '#1890ff'; // 蓝色
          break;
        case 'competitor':
          icon = <ShopOutlined style={{ fontSize: '18px' }} />;
          color = '#722ed1'; // 紫色
          break;
        case 'roomStatus':
          icon = <AppstoreOutlined style={{ fontSize: '18px' }} />;
          color = '#eb2f96'; // 粉色
          break;
        default:
          icon = <DollarOutlined style={{ fontSize: '18px' }} />;
          color = '#52c41a'; // 绿色
      }
      
      return (
        <Avatar size="large" style={{ backgroundColor: color }}>
          {icon}
        </Avatar>
      );
    }
    
    return (
      <Avatar size="large" style={{ backgroundColor: '#1890ff' }}>
        <BellOutlined style={{ fontSize: '18px' }} />
      </Avatar>
    );
  };

  // 获取状态标签
  const getStatusTag = (status: string) => {
    switch (status) {
      case 'warning':
        return <Tag color="red">预警</Tag>;
      case 'alert':
        return <Tag color="orange">提醒</Tag>;
      case 'info':
        return <Tag color="blue">信息</Tag>;
      default:
        return null;
    }
  };

  // 获取子类型标签
  const getSubTypeTag = (notification: NotificationItem) => {
    if (notification.type !== 'priceChange' || !notification.subType) return null;
    
    switch (notification.subType) {
      case 'timePoint':
        return <Tag color="#1890ff">时间点</Tag>;
      case 'competitor':
        return <Tag color="#722ed1">竞品联动</Tag>;
      case 'roomStatus':
        return <Tag color="#eb2f96">房态变化</Tag>;
      default:
        return null;
    }
  };

  // 根据通知类型过滤通知
  const inventoryNotifications = notifications.filter(item => item.type === 'inventory');
  const orderSpeedNotifications = notifications.filter(item => item.type === 'orderSpeed');
  const priceChangeNotifications = notifications.filter(item => item.type === 'priceChange');
  const unreadCount = notifications.filter(item => !item.isRead).length;

  // 返回上一页
  const goBack = () => {
    navigate(-1);
  };

  // 渲染单个通知项
  const renderNotificationItem = (notification: NotificationItem) => (
    <List.Item 
      onClick={() => handleNotificationClick(notification)}
      style={{ 
        cursor: 'pointer',
        backgroundColor: notification.isRead ? 'white' : 'rgba(82, 156, 138, 0.05)',
        marginBottom: '12px',
        borderRadius: '8px',
        padding: '16px',
        border: notification.isRead ? '1px solid #f0f0f0' : '1px solid rgba(82, 156, 138, 0.2)',
      }}
    >
      <List.Item.Meta
        avatar={getNotificationIcon(notification)}
        title={
          <Space style={{ marginBottom: '4px' }}>
            <Text strong style={{ fontSize: '16px' }}>{notification.title}</Text>
            {!notification.isRead && <Badge status="processing" color="#529C8A" />}
            {getStatusTag(notification.status)}
            {getSubTypeTag(notification)}
          </Space>
        }
        description={
          <>
            <Text style={{ display: 'block', marginBottom: '10px', fontSize: '15px' }}>
              {notification.content}
            </Text>
            <Row>
              <Col span={24}>
                <Space>
                  <ClockCircleOutlined style={{ color: '#529C8A' }} />
                  <Text type="secondary" style={{ fontSize: '13px' }}>
                    {notification.time}
                  </Text>
                </Space>
              </Col>
            </Row>
            <Row>
              <Col span={24} style={{ textAlign: 'right', marginTop: 2 }}>
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  {notification.store}
                </Text>
              </Col>
            </Row>
          </>
        }
      />
    </List.Item>
  );

  const tabItems = [
    {
      key: 'all',
      label: (
        <span style={{ fontSize: '15px', padding: '0 8px' }}>
          <DashboardOutlined />
          全部通知
        </span>
      ),
      children: (
        <List
          itemLayout="horizontal"
          dataSource={notifications}
          renderItem={renderNotificationItem}
        />
      )
    },
    {
      key: 'inventory',
      label: (
        <span style={{ fontSize: '15px', padding: '0 8px' }}>
          <WarningOutlined />
          库存预警
        </span>
      ),
      children: (
        <List
          itemLayout="horizontal"
          dataSource={inventoryNotifications}
          renderItem={renderNotificationItem}
        />
      )
    },
    {
      key: 'orderSpeed',
      label: (
        <span style={{ fontSize: '15px', padding: '0 8px' }}>
          <RiseOutlined />
          订单速度
        </span>
      ),
      children: (
        <List
          itemLayout="horizontal"
          dataSource={orderSpeedNotifications}
          renderItem={renderNotificationItem}
        />
      )
    },
    {
      key: 'priceChange',
      label: (
        <span style={{ fontSize: '15px', padding: '0 8px' }}>
          <DollarOutlined />
          调价提醒
        </span>
      ),
      children: (
        <List
          itemLayout="horizontal"
          dataSource={priceChangeNotifications}
          renderItem={renderNotificationItem}
        />
      )
    }
  ];

  return (
    <div style={{ 
      padding: '16px', 
      backgroundColor: '#f5f5f5', 
      minHeight: '100vh',
      maxWidth: '500px',
      margin: '0 auto'
    }}>
      {/* 顶部导航栏 */}
      <Row style={{ marginBottom: '16px' }}>
        <Col span={24} style={{ display: 'flex', alignItems: 'center' }}>
          <Button 
            type="text" 
            icon={<LeftOutlined />} 
            onClick={goBack}
            style={{ padding: '4px 0', marginRight: '8px' }}
          />
          <Title level={4} style={{ margin: 0 }}>
            <Space>
              <BellOutlined style={{ color: '#529C8A' }} />
              收益管理通知
              {unreadCount > 0 && (
                <Badge count={unreadCount} style={{ backgroundColor: '#ff4d4f' }} />
              )}
            </Space>
          </Title>
        </Col>
      </Row>
      
      <Tabs 
        defaultActiveKey="all"
        items={tabItems}
        animated
        size="large"
        tabBarGutter={24}
        style={{ 
          backgroundColor: '#fff', 
          padding: '12px 12px 0',
          borderRadius: '8px',
          marginBottom: '16px'
        }}
      />
    </div>
  );
};

export default InventoryAlertNotifications; 